import { useState } from 'react'

export type CopyState = 'idle' | 'copied' | 'failed'

/**
 * One button's own clipboard write, and the couple of seconds it spends
 * saying so.
 *
 * `navigator.clipboard` is optional and a rejected write is otherwise an
 * unhandled promise, so a click that changes nothing visible is the worst
 * failure a copy button can have. Both the success and the failure show on
 * the button itself, briefly, rather than nowhere.
 */
export function useCopyState(): [CopyState, (text: string) => Promise<void>] {
  const [state, setState] = useState<CopyState>('idle')

  async function copy(text: string) {
    try {
      if (!navigator.clipboard) throw new Error('no clipboard in this browser')
      await navigator.clipboard.writeText(text)
      setState('copied')
    } catch {
      setState('failed')
    }
    setTimeout(() => setState('idle'), 2000)
  }

  return [state, copy]
}

/** What a copy button says, for the state its own clipboard write is in. */
export function copyLabel(state: CopyState, idle: string): string {
  return state === 'copied' ? 'Copied' : state === 'failed' ? "Couldn't copy" : idle
}
